import { useState, useCallback, useMemo, useId } from "react";
import PropTypes from "prop-types";
import { clickableProps } from "@utils/a11y";
import { useDismissOnClick } from "@hooks/useDismissOnClick";
import "@css/TaskHeatMap.css";

const weekDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const monthNames = [
    "Янв","Фев","Мар","Апр","Май","Июн",
    "Июл","Авг","Сен","Окт","Ноя","Дек",
];

const tooltipStyle = {
    position: "fixed",
    transform: "translateX(-50%)",
    background: "#333",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: "4px",
    pointerEvents: "none",
    zIndex: 1000,
    whiteSpace: "nowrap",
    fontSize: "12px",
};

// Year picker + 12 month calendars of day cells + click tooltip. Shared by
// TaskHeatMap (puzzles solved per day) and StatsHeatMaps (daily goals); each
// passes its own colours and cell text.
const YearHeatmap = ({ title, dates, getCellTitle, getCellStyle }) => {
    const fid = useId();

    const years = useMemo(() => {
        const unique = new Set(dates.map(d => new Date(d).getFullYear()));
        return Array.from(unique).sort((a, b) => a - b);
    }, [dates]);

    // Latest year until the user picks one
    const [pickedYear, setPickedYear] = useState(null);
    const selectedYear = pickedYear ?? years.at(-1) ?? null;
    const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0 });

    useDismissOnClick(tooltip.visible, () => setTooltip(t => ({ ...t, visible: false })));

    const handleCellClick = useCallback((event, iso) => {
        event.stopPropagation();
        const rect = event.target.getBoundingClientRect();
        setTooltip({
            visible: true,
            text: getCellTitle(iso),
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
        });
    }, [getCellTitle]);

    const monthsData = useMemo(() => {
        if (!selectedYear) return [];

        const result = [];
        for (let m = 0; m < 12; m++) {
            const days = [];
            const daysInMonth = new Date(selectedYear, m + 1, 0).getDate();
            const firstDay = new Date(selectedYear, m, 1).getDay(); // 0=Вс, 1=Пн ...
            const offset = firstDay === 0 ? 6 : firstDay - 1;

            // пустые ячейки до 1-го числа
            for (let i = 0; i < offset; i++) {
                days.push(<div key={`empty-${m}-${i}`} className="heatmap-cell empty" />);
            }

            for (let d = 1; d <= daysInMonth; d++) {
                const iso = `${selectedYear}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                days.push(
                    <div
                        key={iso}
                        title={getCellTitle(iso)}
                        className="heatmap-cell"
                        style={getCellStyle(iso)}
                        {...clickableProps((e) => handleCellClick(e, iso))}
                    />
                );
            }

            result.push(
                <div key={m} className="heatmap-month">
                    <div className="month-label">{monthNames[m]}</div>
                    <div className="weekdays-row">
                        {weekDays.map(day => (
                            <div key={`${m}-${day}`} className="weekday-header">{day}</div>
                        ))}
                    </div>
                    <div className="month-grid">{days}</div>
                </div>
            );
        }
        return result;
    }, [selectedYear, getCellTitle, getCellStyle, handleCellClick]);

    return (
        <div>
            {years.length > 0 && selectedYear ? <div>
                <h2>{title}</h2>
                <label htmlFor={`${fid}-year`}>Выберите год: </label>
                <select
                    id={`${fid}-year`}
                    value={selectedYear}
                    onChange={e => setPickedYear(Number(e.target.value))}
                >
                    {years.map(year => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div> : null}

            <div className="heatmap-wrapper">
                <div className="year-grid">{monthsData}</div>
                {tooltip.visible ? (
                    <div className="tooltip" style={{ ...tooltipStyle, top: tooltip.y, left: tooltip.x }}>
                        {tooltip.text}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

YearHeatmap.propTypes = {
    title: PropTypes.node.isRequired,
    /** ISO days ("YYYY-MM-DD") that have data; the year picker offers their years. */
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** iso -> text for the cell's title and click tooltip. Keep it stable (useCallback). */
    getCellTitle: PropTypes.func.isRequired,
    /** iso -> inline style (background, border) for the cell. Keep it stable (useCallback). */
    getCellStyle: PropTypes.func.isRequired,
};

export default YearHeatmap;
